import React, { cloneElement, useEffect, useRef, useState } from "react";
import type { TranslatedString } from "@i18n";
import ToolTipComponent from ".";

const SLOT_PROPS = { popper: { sx: { zIndex: 10000 } } };

interface ClippedTooltipProps {
  content: TranslatedString;
  /** The element that clips the label. It is also the anchor, so the tooltip sits on the visible box (CUBE-28079). */
  children: React.ReactElement<{ ref?: React.Ref<HTMLElement> }>;
}

/**
 * Tooltip for a list option's label that opens only when that label is actually clipped, and closes on any scroll
 * so it never floats away from a scrolled list. Used by SelectDropDown and AutoComplete options.
 */
export default function ClippedTooltip({ content, children }: ClippedTooltipProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open || !content) return;
    const dismiss = () => setOpen(false);
    window.addEventListener("scroll", dismiss, true);
    return () => window.removeEventListener("scroll", dismiss, true);
  }, [open, content]);

  const openIfClipped = () => {
    const el = anchorRef.current;
    // 1px tolerance: fractional rem sizes round to a phantom pixel on a label that exactly fits
    if (el && (el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1)) setOpen(true);
  };

  return (
    <ToolTipComponent
      slotProps={SLOT_PROPS}
      content={content}
      open={open}
      onOpen={openIfClipped}
      onClose={() => setOpen(false)}
    >
      {cloneElement(children, { ref: anchorRef })}
    </ToolTipComponent>
  );
}
